import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "sonner";
import { Calculator } from "lucide-react";

export default function MarkupCalculator() {
  const [formData, setFormData] = useState({
    part_cost: "",
    quantity: "1",
    part_source: ""
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [partSources, setPartSources] = useState([]);

  useEffect(() => {
    fetchPartSources();
  }, []);

  const fetchPartSources = async () => {
    try {
      const response = await axios.get("/settings/part-sources");
      const enabledSources = (response.data.sources || []).filter(s => s.enabled);
      setPartSources(enabledSources);
      // Set default to first enabled source
      if (enabledSources.length > 0) {
        setFormData(prev => ({ ...prev, part_source: enabledSources[0].name }));
      }
    } catch (error) {
      console.error("Failed to fetch part sources:", error);
    }
  };

  const handleCalculate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post("/calculator/markup", {
        part_cost: parseFloat(formData.part_cost),
        quantity: parseInt(formData.quantity),
        part_source: formData.part_source
      });
      setResult(response.data);
    } catch (error) {
      toast.error("Calculation failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Input Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="w-5 h-5" />
            Markup Calculator
          </CardTitle>
          <CardDescription>Calculate selling price with markup and shipping</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleCalculate} className="space-y-4">
            <div>
              <Label htmlFor="part_cost">Part Cost ($)</Label>
              <Input
                id="part_cost"
                data-testid="calc-part-cost-input"
                type="number"
                step="0.01"
                value={formData.part_cost}
                onChange={(e) => setFormData({ ...formData, part_cost: e.target.value })}
                required
                placeholder="0.00"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="quantity">Quantity</Label>
              <Input
                id="quantity"
                data-testid="calc-quantity-input"
                type="number"
                min="1"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="part_source">Part Source</Label>
              {partSources.length > 0 ? (
                <select
                  id="part_source"
                  data-testid="calc-part-source-select"
                  value={formData.part_source}
                  onChange={(e) => setFormData({ ...formData, part_source: e.target.value })}
                  className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
                >
                  {partSources.map(source => (
                    <option key={source.id} value={source.name}>{source.name}</option>
                  ))}
                </select>
              ) : (
                <p className="mt-1 text-sm text-slate-500 italic">No part sources available</p>
              )}
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={loading}
              data-testid="calc-calculate-button"
            >
              {loading ? "Calculating..." : "Calculate"}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Results */}
      {result && (
        <Card className="bg-gradient-to-br from-violet-50 to-purple-50 border-violet-200">
          <CardHeader>
            <CardTitle className="text-violet-900">Calculation Results</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="flex justify-between pb-2 border-b border-violet-200">
                <span className="text-sm text-slate-600">Base Cost:</span>
                <span className="font-semibold" data-testid="calc-result-base-cost">${result.base_cost.toFixed(2)}</span>
              </div>
              <div className="flex justify-between pb-2 border-b border-violet-200">
                <span className="text-sm text-slate-600">After Tax (8%):</span>
                <span className="font-semibold" data-testid="calc-result-cost-with-tax">${result.cost_with_tax.toFixed(2)}</span>
              </div>
              <div className="flex justify-between pb-2 border-b border-violet-200">
                <span className="text-sm text-slate-600">Markup ({result.markup_percentage}%):</span>
                <span className="font-semibold text-green-600" data-testid="calc-result-markup">+${result.markup_amount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between pb-2 border-b border-violet-200">
                <span className="text-sm text-slate-600">Subtotal:</span>
                <span className="font-semibold" data-testid="calc-result-subtotal">${result.subtotal.toFixed(2)}</span>
              </div>
              {result.additional_stock_fee > 0 && (
                <div className="flex justify-between pb-2 border-b border-violet-200">
                  <span className="text-sm text-slate-600">Stock Fee (8%):</span>
                  <span className="font-semibold" data-testid="calc-result-stock-fee">+${result.additional_stock_fee.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between pb-2 border-b border-violet-200">
                <span className="text-sm text-slate-600">Shipping:</span>
                <span className="font-semibold" data-testid="calc-result-shipping">+${result.shipping.toFixed(2)}</span>
              </div>
              <div className="flex justify-between pt-2">
                <span className="font-semibold text-slate-700">Total Per Unit:</span>
                <span className="text-xl font-bold text-violet-700" data-testid="calc-result-total-per-unit">${result.total_per_unit.toFixed(2)}</span>
              </div>
              <div className="flex justify-between pt-2 border-t-2 border-violet-300">
                <span className="text-lg font-bold text-slate-900">Total Price (×{result.quantity}):</span>
                <span className="text-2xl font-bold text-violet-900" data-testid="calc-result-total-price">${result.total_price.toFixed(2)}</span>
              </div>
            </div>

            {/* Breakdown */}
            <div className="mt-6 p-4 bg-white rounded-lg border border-violet-200">
              <h4 className="font-semibold mb-2 text-slate-700">Calculation Breakdown:</h4>
              <div className="space-y-1 text-sm text-slate-600">
                <p>{result.breakdown.description}</p>
                {result.breakdown.minimum_applied && (
                  <p className="font-semibold text-amber-600 pt-2">
                    * Minimum $25 price applied
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
